/**
 * Outbound allow-list: only myosh origins receive a `traceparent`. Third
 * parties (presigned-S3, external IdP, analytics) must NEVER receive trace
 * context, so trace ids don't leak outside myosh infrastructure.
 */

const MYOSH_ROOT_HOST = 'myosh.com';
const MYOSH_SUBDOMAIN_SUFFIX = '.myosh.com';

const isMyoshHost = (hostname: string): boolean =>
  hostname === MYOSH_ROOT_HOST || hostname.endsWith(MYOSH_SUBDOMAIN_SUFFIX);

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    // Relative or malformed URL — we cannot verify the origin, so deny.
    return null;
  }
};

/**
 * True when `targetUrl` should carry a `traceparent`:
 * - any `*.myosh.com` origin (static allow-list, valid before cluster config
 *   hydrates), or
 * - an exact origin match against `allowedBaseUrl` (the resolved
 *   `clusterConfig.apiBaseUrl`, which may be a custom non-`myosh.com` domain
 *   for some clusters).
 *
 * A relative or malformed `targetUrl` is denied.
 */
export const isTraceableOrigin = (targetUrl: string, allowedBaseUrl?: string): boolean => {
  const target = parseUrl(targetUrl);
  if (!target) {
    return false;
  }

  if (isMyoshHost(target.hostname)) {
    return true;
  }

  if (allowedBaseUrl) {
    const allowed = parseUrl(allowedBaseUrl);
    if (allowed && allowed.origin === target.origin) {
      return true;
    }
  }

  return false;
};
